/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

export enum PlaybackDataKey {
  /**
   * Variant of the video
   */
  VIDEO_VARIANT = 'video_variant',
  /**
   * Variant of the audio
   */
  AUDIO_VARIANT = 'audio_variant',
  /**
   * Video codec
   */
  VIDEO_CODEC = 'video_codec',
  /**
   * Audio codec
   */
  AUDIO_CODEC = 'audio_codec',
  /**
   * What is the source of the playback
   */
  PLAYBACK_SOURCE = 'playback_source',
  /**
   * The players current time with respect to origin content on a SSAI stream.
   */
  CONTENT_PLAYHEAD_POSITION_MILLISECONDS = 'content_playhead_position_ms',
  /**
   * The original content duration on a SSAI stream  (eliminating ads duration).
   */
  CONTENT_DURATION_MILLISECONDS = 'content_duration_ms',
  /**
   * Unique identifier for the playback session.
   */
  PLAYBACK_SESSION_ID = 'playback_session_id',
  /**
   * Unique identifier for the program playback session.
   */
  PROGRAM_PLAYBACK_SESSION_ID = 'program_playback_session_id',
  /**
   * Name of the player being used.
   */
  PLAYER_NAME = 'player_name',
  /**
   * URL of the playback stream.
   */
  PLAYBACK_URL = 'playback_url',
  /**
   * Name of the DRM system in use.
   */
  DRM_NAME = 'drm_name',
  /**
   * Version of the DRM system.
   */
  DRM_VERSION = 'drm_version',
  /**
   * Video resolution of the playback.
   */
  VIDEO_RESOLUTION = 'video_resolution',
  /**
   * Current bandwidth in kilobits per second.
   */
  CURRENT_BANDWIDTH_KB = 'current_bandwidth_kb',
  /**
   * Highest variant bandwidth in kilobits per second.
   */
  HIGHEST_VARIANT_BANDWIDTH_KB = 'highest_variant_bandwidth_kb',
  /**
   * Lowest variant bandwidth in kilobits per second.
   */
  LOWEST_VARIANT_BANDWIDTH_KB = 'lowest_variant_bandwidth_kb',
  /**
   * Last played variant stream.
   */
  LAST_PLAYED_VARIANT = 'last_played_variant',
  /**
   * Last attempted variant stream.
   */
  LAST_ATTEMPTED_VARIANT = 'last_attempted_variant',
  /**
   * Time when playback started, in seconds.
   */
  PLAYBACK_STARTED_TIME_SEC = 'playback_started_time_sec',
  /**
   * Language of the audio track.
   */
  AUDIO_LANGUAGE = 'audio_language',
  /**
   * Language of the subtitle track.
   */
  SUBTITLE_LANGUAGE = 'subtitle_language',
  /**
   * Indicates if the playback is a free preview.
   */
  IS_FREE_PREVIEW = 'is_free_preview',
  /**
   * Duration of the playback in seconds.
   */
  PLAYBACK_DURATION_SEC = 'playback_duration_sec',
  /**
   * Duration of the program playback in seconds.
   */
  PROGRAM_PLAYBACK_DURATION_SEC = 'program_playback_duration_sec',
  /**
   * Mode of playback (e.g., live, on-demand).
   */
  PLAYBACK_MODE = 'playback_mode',
  /**
   * Source of the playback.
   */
  SOURCE = 'source',
  /**
   * Indicates if closed captions are enabled.
   */
  CC_ENABLED = 'cc_enabled',
  /**
   * Indicates if auto preview is enabled.
   */
  AUTO_PREVIEW = 'auto_preview',
  /**
   * Current playback position in seconds.
   */
  CURRENT_POSITION = 'current_position',
  /**
   * Name of the internal player implementation.
   */
  INTERNAL_PLAYER_NAME = 'internal_player_name',
  /**
   * Version of the internal player implementation.
   */
  INTERNAL_PLAYER_VERSION = 'internal_player_version',
  /**
   * The original content duration on a SSAI stream  (eliminating ads duration).
   */
  CONTENT_DURATION_SEC = 'content_duration_sec',

  /**
   * Video Startup Time metrics (VST) in milliseconds.
   */
  VST_PLAYER_PREPARE_MS = 'vst_start_ms',
  /**
   * Time taken to load the media stream in milliseconds.
   */
  VST_STREAMLOAD_MS = 'vst_streamload_ms',
  /**
   * Time taken to fetch or renew the DRM license in milliseconds.
   */
  VST_LICENSE_MS = 'vst_license_ms',
  /**
   * Time spent on preroll ads before playback in milliseconds.
   */
  VST_PREROLL_MS = 'vst_preroll_ms',
  /**
   * Unique identifier for the ad session.
   */
  AD_SESSION_ID = 'ad_session_id',
  /**
   * Engagement score of the playback.
   */
  ENGAGEMENT_SCORE = 'engagement_score',
}
